import React, { useState, useContext, useEffect } from "react";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import config from "../configs/config";
import "./styles/ManageCrossBreaks.css";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPlusCircle,
  faTimes,
  faGripVertical,
  faTrash,
  faPaste,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "./Loader";

function Banner() {
  const { dataGroup, projectId, banners, setBanners } =
    useContext(VariableContext);
  const {
    activePage,
    setActivePage,
    selectedBannerIndex,
    setSelectedBannerIndex,
    isTotal,
    setIsTotal,
    loading,
    setLoading,
  } = useContext(FetchingFreq);
  const [sigLetters, setSigLetters] = useState(
    banners && banners[selectedBannerIndex]
      ? banners[selectedBannerIndex].sigcompare
      : ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteData, setPasteData] = useState("");
  const [draggedRow, setDraggedRow] = useState(null);

  useEffect(() => {
    setActivePage("Banner");
  }, [setActivePage]);

  useEffect(() => {
    if (banners && banners[selectedBannerIndex]) {
      setSigLetters(banners[selectedBannerIndex].sigcompare || "");
    }
  }, [selectedBannerIndex, banners]);

  const removeRowFromBanner = (bannerIndex, rowIndex) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return {
          ...banner,
          rows: banner.rows.filter((_, rIndex) => rIndex !== rowIndex),
        };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  // Drag and drop functions
  const handleDragStart = (e, bannerIndex, rowIndex) => {
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({ bannerIndex, rowIndex })
    );
    e.currentTarget.classList.add("dragging");
    setDraggedRow(rowIndex);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = (e, targetBannerIndex, targetRowIndex) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");

    const draggedData = JSON.parse(e.dataTransfer.getData("text/plain"));
    const { bannerIndex: sourceBannerIndex, rowIndex: sourceRowIndex } =
      draggedData;

    // Only process if within the same banner
    if (
      sourceBannerIndex === targetBannerIndex &&
      sourceRowIndex !== targetRowIndex
    ) {
      const updatedBanners = banners.map((banner, index) => {
        if (index === targetBannerIndex) {
          const newRows = [...banner.rows];
          const [draggedItem] = newRows.splice(sourceRowIndex, 1);

          // Calculate the new position
          let newPosition = targetRowIndex;
          if (sourceRowIndex < targetRowIndex) {
            // When moving down, adjust position since we removed an item above
            newPosition = targetRowIndex - 1;
          }

          newRows.splice(newPosition, 0, draggedItem);

          // Update letters for all rows
          const rowsWithUpdatedLetters = newRows.map((row, idx) => ({
            ...row,
            letter: getAlphabeticIdentifier(idx),
          }));

          return { ...banner, rows: rowsWithUpdatedLetters };
        }
        return banner;
      });

      setBanners(updatedBanners);
    }

    // Remove dragging classes
    document.querySelectorAll(".dragging, .drag-over").forEach((el) => {
      el.classList.remove("dragging", "drag-over");
    });
    setDraggedRow(null);
  };

  const removeAllRowsForVariable = (bannerIndex, headerText) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return {
          ...banner,
          rows: banner.rows.filter((row) => row.headerText !== headerText),
        };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  // New function to delete a banner with confirmation
  const deleteBanner = (bannerIndex) => {
    const bannerName = banners[bannerIndex].name || `Banner ${bannerIndex + 1}`;

    if (
      window.confirm(
        `Are you sure you want to delete "${bannerName}"? This action cannot be undone.`
      )
    ) {
      const updatedBanners = banners.filter(
        (_, index) => index !== bannerIndex
      );
      setBanners(updatedBanners);

      // Adjust selected banner index if needed
      if (selectedBannerIndex === bannerIndex) {
        if (updatedBanners.length === 0) {
          setSelectedBannerIndex(null);
        } else if (selectedBannerIndex >= updatedBanners.length) {
          setSelectedBannerIndex(updatedBanners.length - 1);
        }
      }
    }
  };

  const selectBanner = (bannerIndex) => {
    setSelectedBannerIndex(bannerIndex);
  };

  const renderTabs = () => {
    if (banners) {
      return banners.map((banner, index) => (
        <div key={index} className="banner-tab-wrapper">
          <button
            onClick={() => selectBanner(index)}
            className={selectedBannerIndex === index ? "active" : ""}
          >
            {banner.name || `Banner ${index + 1}`}
          </button>
          <button
            className="delete-banner-btn"
            onClick={() => deleteBanner(index)}
            title={`Delete ${banner.name || `Banner ${index + 1}`}`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      ));
    }
  };

  const addBanner = () => {
    const safeBanners = Array.isArray(banners) ? banners : [];
    const newIndex = safeBanners.length + 1;
    const newBanner = {
      name: `Banner ${newIndex}`,
      rows: [{ headerText: "Total", text: "Total", letter: "A", logic: "ALL" }],
      minbase: "",
      lowbase: "",
      minbaseSymbol: "**",
      lowbaseSymbol: "*",
      isCompliment: false,
    };
    setBanners([...safeBanners, newBanner]);
    setSelectedBannerIndex(safeBanners.length);
  };

  const handleBannerNameChange = (bannerIndex, newName) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, name: newName };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleMinBaseChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, minbase: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleMinBaseSymbolChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, minbaseSymbol: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleIsComplimentChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, isCompliment: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleLowBaseChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, lowbase: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleLowBaseSymbolChange = (bannerIndex, value) => {
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, lowbaseSymbol: value };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const addRowToBanner = (bannerIndex) => {
    const newRow = { headerText: "", text: "", logic: "" };
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return {
          ...banner,
          rows: [...banner.rows, newRow],
        };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const handleRowChange = (bannerIndex, rowIndex, key, value) => {
    const updatedBanners = banners.map((banner, bIndex) => {
      if (bIndex === bannerIndex) {
        const updatedRows = banner.rows.map((row, rIndex) => {
          if (rIndex === rowIndex) {
            return { ...row, [key]: value };
          }
          return row;
        });
        return { ...banner, rows: updatedRows };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const TextWithIdentifier = ({ value, identifier, onChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value);

    const handleChange = (e) => {
      setInputValue(e.target.value);
    };

    const handleBlur = () => {
      onChange(inputValue);
      setIsEditing(false);
    };

    return (
      <input
        type="text"
        value={isEditing ? inputValue : `${inputValue} [${identifier}]`}
        onChange={handleChange}
        onFocus={() => setIsEditing(true)}
        onBlur={handleBlur}
      />
    );
  };

  const getAlphabeticIdentifier = (index) => {
    let identifier = "";
    while (index >= 0) {
      identifier = String.fromCharCode((index % 26) + 65) + identifier;
      index = Math.floor(index / 26) - 1;
    }
    return identifier;
  };

  const updateLetters = (bannerIndex, newletters) => {
    setSigLetters(newletters);
    const updatedBanners = banners.map((banner, index) => {
      if (index === bannerIndex) {
        return { ...banner, sigcompare: newletters };
      }
      return banner;
    });
    setBanners(updatedBanners);
  };

  const generateSigLetters = () => {
    if (!banners || banners.length === 0 || !banners[selectedBannerIndex])
      return "";
    let letternew = "";
    let prevHeader = null;

    const updatedBanners = banners.map((banner, bannerIndex) => {
      if (bannerIndex === selectedBannerIndex) {
        const updatedRows = banner.rows.map((row, index) => {
          const letter = getAlphabeticIdentifier(index);
          if (prevHeader && row.headerText !== prevHeader) {
            letternew += " / ";
          } else if (letternew) {
            letternew += ", ";
          }
          letternew += letter;
          prevHeader = row.headerText;

          return { ...row, letter };
        });

        return { ...banner, rows: updatedRows, sigcompare: letternew };
      }
      return banner;
    });

    setBanners(updatedBanners);
    setSigLetters(letternew);
    return letternew;
  };

  const handlePasteExcelData = () => {
    if (!pasteData.trim()) return;

    try {
      const rows = pasteData.split("\n").filter((row) => row.trim() !== "");
      const newRows = rows.map((row) => {
        const [headerText = "", text = "", logic = ""] = row
          .split(/\t|,/)
          .map((item) => item.trim());
        return { headerText, text, logic };
      });

      const updatedBanners = banners.map((banner, index) => {
        if (index === selectedBannerIndex) {
          return {
            ...banner,
            rows: [...banner.rows, ...newRows],
          };
        }
        return banner;
      });

      setBanners(updatedBanners);
      setPasteData("");
      setPasteMode(false);
    } catch (error) {
      console.error("Error processing pasted data:", error);
      alert(
        "Error processing pasted data. Please ensure it is in the correct format."
      );
    }
  };

  const renderSelectedBanner = () => {
    if (banners && selectedBannerIndex !== null) {
      const selectedBanner = banners[selectedBannerIndex];
      const groupedRows = {};
      selectedBanner.rows.forEach((row, index) => {
        if (!groupedRows[row.headerText]) {
          groupedRows[row.headerText] = [];
        }
        groupedRows[row.headerText].push({ ...row, originalIndex: index });
      });

      return (
        <div key={selectedBannerIndex}>
          <input
            type="text"
            value={selectedBanner.name}
            onChange={(e) =>
              handleBannerNameChange(selectedBannerIndex, e.target.value)
            }
            placeholder="Enter Banner Name"
          />

          <button onClick={() => generateSigLetters()}>
            <FontAwesomeIcon icon={faPlusCircle} /> Generate Sig Test Letters
          </button>

          <div>
            <label>Min Base: </label>
            <input
              type="number"
              value={selectedBanner.minbase || ""}
              onChange={(e) =>
                handleMinBaseChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Enter Min Base"
            />
            <input
              type="text"
              value={selectedBanner.minbaseSymbol || "**"}
              onChange={(e) =>
                handleMinBaseSymbolChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Symbol"
              style={{ width: "80px" }}
            />
          </div>

          <div>
            <label>Low Base: </label>
            <input
              type="number"
              value={selectedBanner.lowbase || ""}
              onChange={(e) =>
                handleLowBaseChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Enter Low Base"
            />
            <input
              type="text"
              value={selectedBanner.lowbaseSymbol || "*"}
              onChange={(e) =>
                handleLowBaseSymbolChange(selectedBannerIndex, e.target.value)
              }
              placeholder="Symbol"
              style={{ width: "80px" }}
            />
          </div>

          <div className="toggle-switch">
            <input
              type="checkbox"
              id="toggle-compliment"
              checked={selectedBanner.isCompliment || false}
              onChange={(e) =>
                handleIsComplimentChange(selectedBannerIndex, e.target.checked)
              }
            />
            <label htmlFor="toggle-compliment" className="toggle-label"></label>
            <span className="toggle-text">Compliment Test</span>
          </div>

          <input
            type="text"
            onChange={(e) => updateLetters(selectedBannerIndex, e.target.value)}
            value={sigLetters}
            placeholder="Sig test letters (e.g., A, B, C / D, E, F)"
          />

          {pasteMode ? (
            <div className="paste-excel-area">
              <h4>📋 Paste Excel Data</h4>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#718096",
                  marginBottom: "12px",
                }}
              >
                Paste tab or comma separated data with format: HeaderText, Text,
                Logic
              </p>
              <textarea
                value={pasteData}
                onChange={(e) => setPasteData(e.target.value)}
                placeholder="Example:&#10;Gender&#9;Male&#9;Q1=1&#10;Gender&#9;Female&#9;Q1=2"
                rows={6}
              />
              <div>
                <button onClick={handlePasteExcelData}>
                  <FontAwesomeIcon icon={faPlusCircle} /> Add Rows
                </button>
                <button onClick={() => setPasteMode(false)}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>Drag</th>
                    <th style={{ width: "20%" }}>Header Text</th>
                    <th style={{ width: "25%" }}>Text</th>
                    <th style={{ width: "30%" }}>Logic</th>
                    <th style={{ width: "80px", textAlign: "center" }}>
                      Remove
                    </th>{" "}
                    {/* Fixed width */}
                    <th style={{ width: "80px", textAlign: "center" }}>
                      Delete All
                    </th>{" "}
                    {/* Fixed width */}
                  </tr>
                </thead>
                <tbody>
                  {selectedBanner.rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      draggable="true"
                      onDragStart={(e) =>
                        handleDragStart(e, selectedBannerIndex, rowIndex)
                      }
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) =>
                        handleDrop(e, selectedBannerIndex, rowIndex)
                      }
                      className={draggedRow === rowIndex ? "dragging" : ""}
                    >
                      <td style={{ textAlign: "center", cursor: "move" }}>
                        <FontAwesomeIcon
                          icon={faGripVertical}
                          className="drag-handle"
                          title="Drag to reorder"
                          style={{ color: "#718096", fontSize: "16px" }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.headerText}
                          onChange={(e) =>
                            handleRowChange(
                              selectedBannerIndex,
                              rowIndex,
                              "headerText",
                              e.target.value
                            )
                          }
                          placeholder="e.g., Gender"
                        />
                      </td>
                      <td>
                        <TextWithIdentifier
                          value={row.text}
                          identifier={getAlphabeticIdentifier(rowIndex)}
                          onChange={(newValue) =>
                            handleRowChange(
                              selectedBannerIndex,
                              rowIndex,
                              "text",
                              newValue
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.logic}
                          onChange={(e) =>
                            handleRowChange(
                              selectedBannerIndex,
                              rowIndex,
                              "logic",
                              e.target.value
                            )
                          }
                          placeholder="e.g., Q1=1"
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            removeRowFromBanner(selectedBannerIndex, rowIndex)
                          }
                          title="Remove row"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {groupedRows[row.headerText]?.[0]?.originalIndex ===
                          rowIndex && (
                          <button
                            onClick={() =>
                              removeAllRowsForVariable(
                                selectedBannerIndex,
                                row.headerText
                              )
                            }
                            title={`Remove all "${row.headerText}" rows`}
                            className="remove-all-btn"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>{" "}
              <div className="add-buttons">
                <button onClick={() => addRowToBanner(selectedBannerIndex)}>
                  <FontAwesomeIcon icon={faPlusCircle} /> Add Banner Point
                </button>
                <button onClick={() => setPasteMode(true)}>
                  <FontAwesomeIcon icon={faPaste} /> Paste Excel Rows
                </button>
              </div>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const saveBannersToBackend = async () => {
    setLoading(true);
    try {
      const response = await axios.post(config.API_BASE_URL + "/savebanner", {
        banner: JSON.stringify(banners),
        project_id: projectId,
      });

      if (response.status === 200 || response.status === 201) {
        // console.log ('Banners saved successfully');
        setLoading(false);
        alert("✅ Banner Saved Successfully!");
      } else {
        console.error("Failed to save banners");
        setLoading(false);
        alert("❌ Failed to save. Please contact Administrator");
      }
    } catch (error) {
      console.error("Error saving banners:", error);
      setLoading(false);
      alert("❌ Error occurred. Please contact Administrator");
    }
  };

  return (
    <div className="manage-cross-breaks">
      <div className="workspace">
        {loading ? (
          <Loader loading={loading} />
        ) : (
          <>
            <button onClick={addBanner}>
              <FontAwesomeIcon icon={faPlus} /> Add New Banner
            </button>
            <button onClick={saveBannersToBackend}>
              <FontAwesomeIcon icon={faSave} /> Save All Banners
            </button>

            <div className="tabs">{renderTabs()}</div>

            <div className="selected-banner">{renderSelectedBanner()}</div>

            <button
              onClick={saveBannersToBackend}
              style={{ marginTop: "20px" }}
            >
              <FontAwesomeIcon icon={faSave} /> Save All Banners
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Banner;
